import React from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS } from '../constants/types';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import {
    MicrostoreColumnTemplateFragmentFragment,
    MicrostoreColumnTemplateState,
    useUpdateMicrostoreColumnTemplateStateMutation,
} from 'data/__generated__';

export function ArchiveMicrostoreColumnTemplatesPopup({
    microstoreColumnTemplates,
    selectedMicrostoreColumnTemplateIds,
    setSelectedMicrostoreColumnTemplateIds,
    isOpen,
    setIsOpen,
}: {
    microstoreColumnTemplates: MicrostoreColumnTemplateFragmentFragment[];
    selectedMicrostoreColumnTemplateIds: string[];
    setSelectedMicrostoreColumnTemplateIds: (value: React.SetStateAction<string[]>) => void;
    isOpen: boolean;
    setIsOpen: (value: React.SetStateAction<boolean>) => void;
}) {
    const [updateMicrostoreColumnTemplateState, { loading }] = useUpdateMicrostoreColumnTemplateStateMutation();

    const selectedMicrostoreColumnTemplates = microstoreColumnTemplates.filter((microstoreColumnTemplate) =>
        selectedMicrostoreColumnTemplateIds.includes(microstoreColumnTemplate._id),
    );

    async function handleSubmit() {
        const errors: string[] = [];

        for (const microstoreColumnTemplateId of selectedMicrostoreColumnTemplateIds) {
            const { data } = await updateMicrostoreColumnTemplateState({
                variables: {
                    microstoreColumnTemplateId,
                    stateToSet: MicrostoreColumnTemplateState.Archived,
                },
            });

            if (!data) {
                throw new Error("Une erreur est survenue lors de l'archivage des templates de colonne");
            }

            const {
                updateMicrostoreColumnTemplateStateMutation: { errors: mutationErrors },
            } = data;

            if (mutationErrors.length) {
                errors.push(...mutationErrors);
            }
        }

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        } else {
            toast.success(`Template(s) de colonne archivé(s) : ${selectedMicrostoreColumnTemplates.length}`);
        }

        setSelectedMicrostoreColumnTemplateIds([]);
        setIsOpen(false);
    }

    return (
        <TotemPopup title="Êtes-vous sûr.e ?" isOpen={isOpen} setIsOpen={setIsOpen}>
            Vous vous apprêtez à archiver {selectedMicrostoreColumnTemplates.length} template(s) de colonne.
            Souhaitez-vous vraiment archiver le/les template(s) suivant(s) ?
            <MicrostoreColumnTemplatelist>
                {selectedMicrostoreColumnTemplates.map((microstoreColumnTemplate) => {
                    const { _id: microstoreColumnTemplateId, name, type } = microstoreColumnTemplate;

                    return (
                        <MicrostoreColumnTemplate key={microstoreColumnTemplateId}>
                            <MicrostoreColumnTemplateTitle>{name}</MicrostoreColumnTemplateTitle>
                            <MicrostoreColumnTemplateText>
                                Type : {MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS[type]}
                            </MicrostoreColumnTemplateText>
                        </MicrostoreColumnTemplate>
                    );
                })}
            </MicrostoreColumnTemplatelist>
            <SubmitContainer>
                <TotemPrimaryButton onClick={handleSubmit}>
                    {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Confirmer'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </TotemPopup>
    );
}

const MicrostoreColumnTemplatelist = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    overflow: auto;

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const MicrostoreColumnTemplate = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-around;
`;

const MicrostoreColumnTemplateText = styled.div`
    margin-left: 15px;
`;

const MicrostoreColumnTemplateTitle = styled.div`
    font-weight: 800;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
